import React, {useMemo, useState} from 'react';
import {useIntl} from "react-intl";
import {FormikProps} from "formik";
import useInfiniteScrollControl from "../../_common/components/_custom-hooks/useInfiniteScrollControl";
import * as actions from "../_redux/aiReportsActions";
import {useTable} from "react-table";
import CommonGrid from "../../_common/components/CommonGrid";
import * as commonColumnFormatters from '../../_common/column-formatters';
import * as customColumnFormatters from './SavedQueriesActionsColumnFormatter';
import {SavedQueriesProvider, useSavedQueriesContext} from "../context/SavedQueriesContext";

interface FormValues {
  query: string;
}

type FilterValues = {
  query: string;
  reportId: string;
};

export function SavedQueriesTable({handleCloseModal, setFieldValue, applyFilter} : {
  handleCloseModal: () => void;
  setFieldValue: FormikProps<FormValues>['setFieldValue'];
  applyFilter: (values: FilterValues) => void;
}) {
  const intl = useIntl();
  const prefix = 'ai-reports-saved-queries-column';
  const [tableRows, setTableRows] = useState([]);
  const columns = useMemo(() => [
    {
      accessor: 'query', Header: intl.formatMessage({id: `${prefix}-query`, defaultMessage: 'Query'})
    },
    {
      accessor: 'userID', Header: intl.formatMessage({id: `${prefix}-userID`, defaultMessage: 'User'})
    },
    {
      accessor: 'createdAt', Header: intl.formatMessage({id: `${prefix}-createdAt`, defaultMessage: 'Date'})
    },
    {
      accessor: 'actions', Header: ''
    }
  ], [intl]);
  const data = useMemo(() => [
      ...tableRows.map((item: any) => {
        return {
          query: commonColumnFormatters.NormalColumnFormatter(item?.query),
          userID: commonColumnFormatters.BoldColumnFormatter(item?.userID),
          createdAt: commonColumnFormatters.DateColumnFormatter(item?.createdAt, true),
          actions: customColumnFormatters.SavedQueriesActionsColumnFormatter(
            item,
            {handleCloseModal, setFieldValue, applyFilter}
          )
        }
      })
    ],
    [tableRows]
  );

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'savedQueries',
    UIContext: useSavedQueriesContext(),
    getNewEntities: actions.getQueriesList,
    getNextEntities: actions.getNextQueriesList,
    onEntitiesChange: (entities: never[]) => setTableRows(entities),
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (
      <div className="spinner-container">
        <span className="spinner spinner-md spinner-primary" />
      </div>
    ),
    style: {
      overflow: 'inherit',
    }
  });

  const tableInstance = useTable<any>({columns, data});

  return (
    <SavedQueriesProvider>
      <CommonGrid
        infiniteScrollProps={infiniteScrollProps}
        tableProps={tableInstance}
        customTableStyles={'saved-queries'}
      />
    </SavedQueriesProvider>
  );
}